import { SaltIcons } from "@/design-system/salt";
import { APP_NAME, resolvePageTitle } from "./page-titles";
import type { SaltShellIcon } from "./salt-shell-navigation";

/** A single navigation entry — leaf or parent with children. */
export interface NavItemData {
  id: string;
  name: string;
  path: string;
  icon: SaltShellIcon;
  disabled?: boolean;
  children?: readonly NavItemData[];
  /** Additional path prefixes that should mark this leaf active. */
  activePatterns?: readonly string[];
}

// ---------------------------------------------------------------------------
// Flat items (no children)
// ---------------------------------------------------------------------------

export const primaryNavigation: readonly NavItemData[] = [
  {
    id: "corporates",
    name: "Corporates",
    path: "/corporates",
    icon: SaltIcons.BuildingIcon,
    activePatterns: ["/corporate/"],
  },
  { id: "reports", name: "Reports", path: "/reports", icon: SaltIcons.DocumentIcon },
] as const;

// ---------------------------------------------------------------------------
// Parent: Workspaces
// ---------------------------------------------------------------------------

export const workspaceNavigation: readonly NavItemData[] = [
  {
    id: "workspaces",
    name: "Workspaces",
    path: "/workspaces",
    icon: SaltIcons.GridIcon,
    children: [
      {
        id: "issuer-hub",
        name: "Issuer Hub",
        path: "/issuer-hub/pipeline",
        icon: SaltIcons.BankIcon,
        activePatterns: ["/issuer-hub"],
      },
      {
        id: "supplier-onboarding",
        name: "Supplier Onboarding",
        path: "/supplier-onboarding",
        icon: SaltIcons.UserAdminIcon,
      },
      {
        id: "action-center",
        name: "Action Center",
        path: "/action-center",
        icon: SaltIcons.HeadphonesIcon,
        activePatterns: ["/tasks"],
      },
    ],
  },
] as const;

// ---------------------------------------------------------------------------
// Parent: Buyer Onboarding
// ---------------------------------------------------------------------------

export const buyerOnboardingNavigation: readonly NavItemData[] = [
  {
    id: "buyer-onboarding",
    name: "Buyer Onboarding",
    path: "/buyer-onboarding",
    icon: SaltIcons.GuideOpenIcon,
    children: [
      {
        id: "conversation-guide",
        name: "Treasury Guide",
        path: "/conversation-guide",
        icon: SaltIcons.MessageIcon,
      },
      {
        id: "upload-ap-spend",
        name: "Upload AP Spend File",
        path: "/ap-spend-files/upload",
        icon: SaltIcons.UploadIcon,
        activePatterns: ["/ap-spend-files"],
      },
    ],
  },
] as const;

// ---------------------------------------------------------------------------
// Parent: Outreach
// ---------------------------------------------------------------------------

export const outreachNavigation: readonly NavItemData[] = [
  {
    id: "outreach",
    name: "Outreach",
    path: "/outreach",
    icon: SaltIcons.SendIcon,
    children: [
      {
        id: "enrollments",
        name: "Enrollments",
        path: "/enrollments",
        icon: SaltIcons.AddUserIcon,
        activePatterns: ["/approvals"],
      },
      {
        id: "intelligence",
        name: "Shared Suppliers",
        path: "/intelligence",
        icon: SaltIcons.CompareIcon,
      },
    ],
  },
] as const;

// ---------------------------------------------------------------------------
// Parent: Supplier Optimization
// ---------------------------------------------------------------------------

export const supplierOptimizationNavigation: readonly NavItemData[] = [
  {
    id: "supplier-optimization",
    name: "Supplier Optimization",
    path: "/supplier-optimization",
    icon: SaltIcons.MagicWandIcon,
    children: [
      {
        id: "supplier-onboarding-opt",
        name: "Supplier Onboarding",
        path: "/supplier-onboarding",
        icon: SaltIcons.UserAdminIcon,
      },
    ],
  },
] as const;

// ---------------------------------------------------------------------------
// Admin (flat, role-guarded at section level)
// ---------------------------------------------------------------------------

export const adminNavigation: readonly NavItemData[] = [
  {
    id: "admin",
    name: "Admin",
    path: "/admin/settings",
    icon: SaltIcons.SettingsIcon,
    activePatterns: ["/admin"],
  },
] as const;

// ---------------------------------------------------------------------------
// Aggregated export for full-tree consumers
// ---------------------------------------------------------------------------

export const navigation: readonly NavItemData[] = [
  ...primaryNavigation,
  ...workspaceNavigation,
  ...buyerOnboardingNavigation,
  ...outreachNavigation,
  ...supplierOptimizationNavigation,
  ...adminNavigation,
] as const;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Flatten the navigation tree for path lookups. */
function flattenItems(items: readonly NavItemData[]): NavItemData[] {
  const result: NavItemData[] = [];
  for (const item of items) {
    result.push(item);
    if (item.children) result.push(...flattenItems(item.children));
  }
  return result;
}

const flatNavigation = flattenItems(navigation);

/** Resolve the global navigation item ID for the current route. */
export function getActiveTab(pathname: string): string {
  // Check activePatterns first (handles /corporate/, /issuer-hub, etc.)
  for (const item of flatNavigation) {
    if (item.activePatterns?.some((p) => pathname.startsWith(p))) return item.id;
  }
  // Exact match
  const exact = flatNavigation.find((item) => pathname === item.path);
  if (exact) return exact.id;
  // Prefix match
  const prefix = flatNavigation.find(
    (item) => item.path !== "/" && pathname.startsWith(`${item.path}/`),
  );
  return prefix?.id || "corporates";
}

/**
 * Resolve the dashboard header title for the current route.
 *
 * The chrome renders this string as the page's `<h1>` — see `salt-page-chrome.tsx` for
 * the ownership decision — so "no title" is not a cosmetic gap, it is a page with no
 * heading. The route table lives in `page-titles.ts`; this function is only the policy
 * for what happens when the table misses.
 *
 * DEV THROWS. Aaron's call (2026-08-18), and the reason is that the previous fallback
 * was `"Dashboard"`: silent, plausible-looking, and MEASURED as the heading on 14 of the
 * 88 paths declared inside `DashboardLayoutRoute` (see `page-titles.ts` for the full
 * replay). A fallback that reads like a real title is how a sweep like this rots — the
 * next unmapped route looks fine in review and ships.
 *
 * PRODUCTION FALLS BACK to the app name. A missing title is a bad heading; a thrown
 * error inside the shell's `useMemo` is a white screen for a customer, and the two are
 * not the same size of problem.
 *
 * `corporateName` is still accepted because `DashboardLayout` still passes it, but the
 * corporate's name is no longer the title of its sub-pages: it is already the sidebar
 * workspace title, and one title per screen means this one says WHICH PAGE. Dynamic
 * titles that genuinely are data — a vendor's name, a campaign's name — are supplied by
 * the page itself through `usePageTitleOverride`.
 */
export function getPageTitle(pathname: string, corporateName?: string): string {
  void corporateName;

  const title = resolvePageTitle(pathname);
  if (title) return title;

  if (import.meta.env.DEV) {
    throw new Error(
      `No page title is mapped for "${pathname}". Every route rendered inside ` +
        "DashboardLayout must have an entry in src/components/dashboard/page-titles.ts — " +
        "the chrome renders that title as the page's <h1>, so an unmapped route ships a " +
        "page with no heading. Add the route's pattern and title to PAGE_TITLES.",
    );
  }

  return APP_NAME;
}
